import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListSubheader,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import BugReportIcon from "@mui/icons-material/BugReport";
import ReplayIcon from "@mui/icons-material/Replay";
import { useApolloClient, useMutation } from "@apollo/client";
import {
  GET_BACKEND_TASKS,
  LAUNCH_TASK,
  RETRY_TASK,
  tokenOptions,
} from "../../backend/graphql";
import { useAppSelector, useHistoryState } from "../../app/hooks";
import { isReadonly } from "../../app/features/auth/authTypes";
import {
  GraphQLDataGrid,
  GraphQLGridParameters,
} from "../widgets/GraphQLDataGrid";
import { gridActionSx } from "../widgets/gridActionSx";
import ActionButton from "../widgets/ActionButton";
import PageTitle from "../widgets/PageTitle";
import QuickFilter from "../widgets/QuickFilter";
import SectionPaper from "../widgets/SectionPaper";

// The tasks that can be started by hand: the ones the worker runs on their own, without a stone or
// an image to point them at. The ones that need parameters are launched from the screen that has
// them — an analysis from a stone, a recalibration from an image.
const LAUNCHABLE_TASKS: { section: string; tasks: [string, string][] }[] = [
  {
    section: "Maintenance",
    tasks: [
      ["Daily", "Daily"],
      ["FillMissingDates", "Backfill missing image dates"],
      ["ReloadColorEquivalences", "Reload color equivalences"],
      [
        "AnalyzeColorRelationships",
        "Validate color relationships based on data",
      ],
    ],
  },
  {
    section: "Image learning",
    tasks: [
      ["TrainModels", "Train models"],
      ["FitFakeCalibration", "Fit fakecalibration"],
      ["FitGrading", "Fit grading"],
    ],
  },
  {
    section: "Synchronizing",
    tasks: [
      ["DiamondSyncNew", "Fetch new diamonds from Fantasy"],
      ["ResetSync", "Force resync from Fantasy"],
    ],
  },
  {
    section: "Pricing",
    tasks: [
      ["TrainPrices", "Train prices"],
      ["TrainFactors", "Fit factors"],
      ["ValidatePrices", "Validate prices"],
      ["ImportFiles", "Import pricing files from Google Drive"],
    ],
  },
];

// How often the table is brought up to date while it is on screen. Tasks come and go on their
// own — a run started elsewhere, an attempt counted, a failure recorded — so a page opened and
// left alone would quietly show a picture of the moment it was opened.
const REFRESH_MS = 10000;

const Tasks = () => {
  const client = useApolloClient();
  const token = useAppSelector((state) => state.auth.token);
  const readonly = isReadonly(useAppSelector((state) => state.auth.role));
  const [launchTask] = useMutation(LAUNCH_TASK, tokenOptions(token));
  const [retryTask] = useMutation(RETRY_TASK, tokenOptions(token));
  const [errorsOnly, setErrorsOnly] = useHistoryState("errorsOnly", false);
  const [launching, setLaunching] = useState(false);
  const [taskType, setTaskType] = useState("Daily");
  const [details, setDetails] = useState<any | null>(null);
  const [launched, setLaunched] = useState<string | null>(null);

  // Refreshed from here rather than by the grid, which holds its query and its own refetch: the
  // client can re-run an active query by document, whoever issued it, and the grid keeps its
  // filter, its sorting and its page while the rows underneath are replaced.
  useEffect(() => {
    const refresh = setInterval(() => {
      client.refetchQueries({ include: [GET_BACKEND_TASKS] }).catch(() => {});
    }, REFRESH_MS);
    return () => clearInterval(refresh);
  }, [client]);

  const launch = async () => {
    await launchTask({ variables: { taskType, runs: 1 } });
    setLaunching(false);
    // The new task is not an errored one, so show every task or nothing would seem to happen.
    setErrorsOnly(false);
    setLaunched(taskType);
  };

  const gridParameters: GraphQLGridParameters = {
    graphqlQuery: GET_BACKEND_TASKS,
    graphqlItemsPath: (data) => data.backendTasks,
    graphqlFilter: errorsOnly ? "isError == true" : undefined,
    fields: [
      { field: "id", label: "ID", visible: false, width: 300, type: "uuid" },
      {
        field: "createdAt",
        label: "Created",
        type: "dateTime",
        defaultSort: "desc",
      },
      // No width: the task name takes whatever is left, which puts the actions hard against the
      // right edge of the table instead of leaving a gap after them.
      { field: "taskType", label: "Task" },
      {
        // Named for the field the filter searches — the last name — while the cell shows the whole
        // one. Empty where the worker raised the task for itself, as the nightly run does.
        field: "launchedBy.lastName",
        label: "Launched by",
        width: 180,
        valueGetter: (params: any) =>
          params.row.launchedBy
            ? `${params.row.launchedBy.firstName} ${params.row.launchedBy.lastName}`
            : "",
      },
      { field: "isDone", label: "Done", type: "boolean" },
      { field: "isError", label: "Error", type: "boolean" },
      {
        field: "isInteractive",
        label: "Interactive",
        type: "boolean",
        visible: false,
      },
      {
        field: "attempts",
        label: "Attempts",
        type: "number",
        width: 100,
        visible: false,
      },
      { field: "errorMessage", label: "Message", flex: 2, visible: false },
      {
        field: "taskParametersJSon",
        label: "Parameters",
        visible: false,
        flex: 1,
      },
    ],
    // Details and — unless read-only — retry.
    maxActions: readonly ? 1 : 2,
    getActions: ({ row, refetch }) => {
      const actions = [];

      if (row.isError) {
        actions.push(
          <GridActionsCellItem
            icon={<BugReportIcon />}
            label="Details"
            sx={gridActionSx}
            onClick={() => setDetails(row)}
          />,
        );
        // A read-only user reads the error, and launches nothing again.
        if (!readonly)
          actions.push(
            <GridActionsCellItem
              icon={<ReplayIcon />}
              label="Retry"
              sx={gridActionSx}
              onClick={async () => {
                await retryTask({ variables: { id: row.id } });
                await refetch();
              }}
            />,
          );
      }

      return actions;
    },
  };

  return (
    <SectionPaper sx={{ width: "100%" }}>
      <Stack spacing={1} useFlexGap>
        <PageTitle
          actions={
            !readonly && (
              <ActionButton onClick={() => setLaunching(true)}>
                Launch task
              </ActionButton>
            )
          }
        />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <QuickFilter
            active={errorsOnly}
            label="Errors only"
            toggle={(active) => setErrorsOnly(!active)}
          />
        </Stack>
        <GraphQLDataGrid parameters={gridParameters} elevated />
      </Stack>

      <Dialog
        open={launching}
        onClose={() => setLaunching(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Launch a task</DialogTitle>
        <DialogContent>
          <Select
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            value={taskType}
            onChange={(event) => setTaskType(event.target.value)}
          >
            {LAUNCHABLE_TASKS.flatMap((group) => [
              <ListSubheader key={group.section}>
                {group.section}
              </ListSubheader>,
              ...group.tasks.map(([type, label]) => (
                <MenuItem key={type} value={type}>
                  {label}
                </MenuItem>
              )),
            ])}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLaunching(false)}>Cancel</Button>
          <ActionButton onClick={launch}>Launch</ActionButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={details !== null}
        onClose={() => setDetails(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{details?.taskType} failed</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography sx={{ fontWeight: 500 }}>
              {details?.errorMessage}
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Attempts
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>
                {details?.attempts}
              </Typography>
            </Box>
            {details?.taskParametersJSon && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Parameters
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  {details.taskParametersJSon}
                </Typography>
              </Box>
            )}
            {details?.errorTrace && (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflowX: "auto",
                  fontSize: "0.75rem",
                }}
              >
                {details.errorTrace}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetails(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={launched !== null}
        autoHideDuration={4000}
        onClose={() => setLaunched(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setLaunched(null)}>
          {launched} launched.
        </Alert>
      </Snackbar>
    </SectionPaper>
  );
};

export default Tasks;
